import React, { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";

const BouncingBallGame = ({
  ballCount = 12,
  minRadius = 14,
  maxRadius = 30,
  speed = 1.8,
  mouseBreakRadius = 22,
  clickBreakRadius = 90,
  zIndex = 1,
  disabledOnMobile = true,
}) => {
  const canvasRef = useRef(null);
  const cheerRef = useRef(null);

  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);

  // scoreRef keeps the latest score available to the canvas animation loop.
  const scoreRef = useRef(0);
  const nextMilestoneRef = useRef(1000);
  const timeoutRefs = useRef(new Set());

  // IndexedDB is used without Web Storage.
  const DB_NAME = "BouncingBallGameDB";
  const STORE_NAME = "game";
  const SCORE_KEY = "globalScore";

  const openScoreDB = () =>
    new Promise((resolve, reject) => {
      if (typeof window === "undefined" || !window.indexedDB) {
        reject(new Error("IndexedDB is not available"));
        return;
      }

      const request = window.indexedDB.open(DB_NAME, 1);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

  const loadSavedScore = async () => {
    try {
      const db = await openScoreDB();

      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readonly");
        const request =
          transaction.objectStore(STORE_NAME).get(SCORE_KEY);

        request.onsuccess = () => {
          db.close();
          resolve(Number(request.result || 0));
        };

        request.onerror = () => {
          db.close();
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Unable to load saved game score:", error);
      return 0;
    }
  };

  const saveScore = async (newScore) => {
    try {
      const db = await openScoreDB();

      await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readwrite");

        transaction.objectStore(STORE_NAME).put(
          Number(newScore),
          SCORE_KEY
        );

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };

        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };

        transaction.onabort = () => {
          db.close();
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error("Unable to save game score:", error);
    }
  };

  // Load the saved score once when the component is mounted.
  useEffect(() => {
    let mounted = true;

    loadSavedScore().then((savedScore) => {
      if (!mounted) return;

      scoreRef.current = savedScore;
      setScore(savedScore);

      nextMilestoneRef.current =
        (Math.floor(savedScore / 1000) + 1) * 1000;
    });

    return () => {
      mounted = false;
    };
  }, []);


const stopGame = () => {
  setGameStarted(false);

  if (cheerRef.current) {
    cheerRef.current.style.animation = "none";
    cheerRef.current.style.opacity = 0;
  }
};


  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const parent = canvas.parentElement;

    if (!parent) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    let animationFrame = null;
    let width = 0;
    let height = 0;
    const balls = [];
    const particles = [];

    const mouse = {
      x: -1000,
      y: -1000,
      active: false,
    };

    /*
     * ---------------------------------------------------------
     * CONFIG
     * ---------------------------------------------------------
     */

    const MAX_PARTICLES = 400;
    const RESPAWN_DELAY = 300;

    /*
     * ---------------------------------------------------------
     * COLORS
     * ---------------------------------------------------------
     */

    const colors = [
      {
        main: "#d71920",
        light: "#ff6b70",
        dark: "#8e0b10",
      },
      {
        main: "#1565c0",
        light: "#64b5f6",
        dark: "#0d47a1",
      },
      {
        main: "#7b1fa2",
        light: "#ce93d8",
        dark: "#4a148c",
      },
      {
        main: "#00897b",
        light: "#80cbc4",
        dark: "#004d40",
      },
      {
        main: "#ef6c00",
        light: "#ffb74d",
        dark: "#e65100",
      },
    ];

    /*
     * ---------------------------------------------------------
     * RANDOM HELPERS
     * ---------------------------------------------------------
     */

    const random = (min, max) =>
      Math.random() * (max - min) + min;

    const randomColor = () =>
      colors[Math.floor(Math.random() * colors.length)];

    /*
     * ---------------------------------------------------------
     * SAFE TIMEOUT
     *
     * Every timeout is tracked so nothing remains after
     * component unmount.
     * ---------------------------------------------------------
     */





    const addTimeout = (callback, delay) => {
      const timeoutId = setTimeout(() => {
        timeoutRefs.current.delete(timeoutId);

        callback();
      }, delay);

      timeoutRefs.current.add(timeoutId);

      return timeoutId;
    };

    /*
     * ---------------------------------------------------------
     * RESIZE
     * ---------------------------------------------------------
     */

    const resize = () => {
      const rect = parent.getBoundingClientRect();

      width = Math.max(0, rect.width);
      height = Math.max(0, rect.height);

      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    /*
     * ---------------------------------------------------------
     * CREATE BALL
     * ---------------------------------------------------------
     */

    const createBall = () => {
      const radius = random(minRadius, maxRadius);

      let x = random(
        radius,
        Math.max(radius, width - radius)
      );

      let y = random(
        radius,
        Math.max(radius, height - radius)
      );

      let safe = false;
      let attempts = 0;

      while (!safe && attempts < 80) {
        x = random(
          radius,
          Math.max(radius, width - radius)
        );

        y = random(
          radius,
          Math.max(radius, height - radius)
        );

        safe = true;

        for (const ball of balls) {
          const dx = x - ball.x;
          const dy = y - ball.y;

          const distance = Math.sqrt(
            dx * dx + dy * dy
          );

          if (
            distance <
            radius + ball.radius + 10
          ) {
            safe = false;
            break;
          }
        }

        attempts++;
      }

      /*
       * Ball points:
       *
       * +5
       * +10
       * +15
       * +20
       * +25
       * +30
       */

      const points =
        Math.floor(random(5, 31) / 5) * 5;

      const angle = random(0, Math.PI * 2);

      return {
        x,
        y,

        radius,

        vx:
          Math.cos(angle) *
          random(0.7, 1.4) *
          speed,

        vy:
          Math.sin(angle) *
          random(0.7, 1.4) *
          speed,

        color: randomColor(),

        points,

        rotation: random(0, Math.PI * 2),

        pulse: random(0, Math.PI * 2),

        /*
         * Important:
         * This flag replaces the old brokenBalls Set.
         *
         * The old Set could grow forever because every
         * broken ball ID was retained.
         */
        broken: false,
      };
    };

    /*
     * ---------------------------------------------------------
     * INITIALIZE BALLS
     * ---------------------------------------------------------
     */

    const initializeBalls = () => {
      balls.length = 0;

      const isMobile =
        window.innerWidth < 700;

      const count =
        disabledOnMobile && isMobile
          ? 0
          : Math.max(3, ballCount);

      for (let i = 0; i < count; i++) {
        balls.push(createBall());
      }
    };

    /*
     * ---------------------------------------------------------
     * PARTICLES
     * ---------------------------------------------------------
     */

    const addParticle = (particle) => {
      /*
       * Remove oldest particles first.
       *
       * This guarantees that the array can never grow
       * beyond MAX_PARTICLES.
       */

      if (particles.length >= MAX_PARTICLES) {
        particles.splice(
          0,
          particles.length - MAX_PARTICLES + 1
        );
      }

      particles.push(particle);
    };

    const createExplosion = (
      x,
      y,
      color,
      amount = 14
    ) => {
      const particleCount = Math.min(
        Math.max(1, amount),
        20
      );

      for (let i = 0; i < particleCount; i++) {
        const angle = random(
          0,
          Math.PI * 2
        );

        const velocity = random(1, 4);

        addParticle({
          x,
          y,

          vx:
            Math.cos(angle) *
            velocity,

          vy:
            Math.sin(angle) *
            velocity,

          size: random(2, 5),

          life: 1,

          color:
            color.main || "#d71920",
        });
      }
    };

    /*
     * ---------------------------------------------------------
     * CELEBRATION
     * ---------------------------------------------------------
     */

    const showCheer = (newScore) => {
      /*
       * Only trigger when a new 1000-point milestone
       * has been crossed.
       */

      if (
        newScore <
        nextMilestoneRef.current
      ) {
        return;
      }

      const milestone =
        nextMilestoneRef.current;

      nextMilestoneRef.current += 1000;

      const cheer = cheerRef.current;

      if (!cheer) return;

      const title =
        cheer.querySelector(
          ".cheer-title"
        );

      const subtitle =
        cheer.querySelector(
          ".cheer-subtitle"
        );

      if (title) {
        title.textContent =
          "🎉 AMAZING!";
      }

      if (subtitle) {
        subtitle.textContent =
          `${milestone.toLocaleString()} Points Reached!`;
      }

      /*
       * Restart CSS animation.
       */

      cheer.style.animation = "none";

      void cheer.offsetWidth;

      cheer.style.animation =
        "cheerPop 3.5s ease forwards";

      /*
       * Celebration particles.
       */

      for (let i = 0; i < 40; i++) {
        const angle = random(
          0,
          Math.PI * 2
        );

        const velocity = random(2, 7);

        addParticle({
          x: width / 2,

          y: height * 0.25,

          vx:
            Math.cos(angle) *
            velocity,

          vy:
            Math.sin(angle) *
            velocity,

          size: random(2, 6),

          life: 1,

          color:
            colors[
              Math.floor(
                Math.random() *
                  colors.length
              )
            ].main,
        });
      }

      /*
       * Stop animation after it finishes.
       *
       * This timeout is also tracked.
       */

      addTimeout(() => {
        if (cheer) {
          cheer.style.animation = "none";
        }
      }, 3600);
    };

    /*
     * ---------------------------------------------------------
     * BREAK BALL
     * ---------------------------------------------------------
     */

    const breakBall = (
      index,
      points = 10
    ) => {
      const ball = balls[index];

      if (!ball) return;

      /*
       * Prevent duplicate break events.
       *
       * No Set is required.
       * The object itself is marked as broken.
       */

      if (ball.broken) return;

      ball.broken = true;

      /*
       * Explosion.
       */

      createExplosion(
        ball.x,
        ball.y,
        ball.color,
        Math.min(
          14,
          Math.round(ball.radius * 0.7)
        )
      );

      /*
       * Score.
       */

      scoreRef.current += points;
      setScore(scoreRef.current);
      saveScore(scoreRef.current);

      /*
       * Milestone.
       */

      showCheer(scoreRef.current);

      /*
       * Remove ball immediately.
       */

      balls.splice(index, 1);

      /*
       * Respawn after a short delay.
       */

      addTimeout(() => {
        if (
          width > 0 &&
          height > 0
        ) {
          balls.push(createBall());
        }
      }, RESPAWN_DELAY);
    };

    /*
     * ---------------------------------------------------------
     * MOUSE MOVE
     * ---------------------------------------------------------
     */

    const handleMouseMove = (event) => {
      const rect =
        canvas.getBoundingClientRect();

      mouse.x =
        event.clientX -
        rect.left;

      mouse.y =
        event.clientY -
        rect.top;

      mouse.active = true;
    };

    /*
     * ---------------------------------------------------------
     * MOUSE LEAVE
     * ---------------------------------------------------------
     */

    const handleMouseLeave = () => {
      mouse.active = false;

      mouse.x = -1000;
      mouse.y = -1000;
    };

    /*
     * ---------------------------------------------------------
     * MOUSE CLICK
     * ---------------------------------------------------------
     */

    const handleClick = () => {
      if (!mouse.active) return;

      /*
       * Click explosion.
       */

      createExplosion(
        mouse.x,
        mouse.y,
        {
          main: "#d71920",
        },
        25
      );

      /*
       * Break all balls inside click radius.
       *
       * Reverse iteration makes splice safe.
       */

      for (
        let i = balls.length - 1;
        i >= 0;
        i--
      ) {
        const ball = balls[i];

        if (!ball) continue;

        const dx =
          ball.x - mouse.x;

        const dy =
          ball.y - mouse.y;

        const distance =
          Math.sqrt(
            dx * dx + dy * dy
          );

        if (
          distance <
          clickBreakRadius
        ) {
          breakBall(
            i,
            ball.points
          );
        }
      }
    };

    /*
     * ---------------------------------------------------------
     * BALL COLLISION
     * ---------------------------------------------------------
     */

    const handleBallCollision = (
      a,
      b
    ) => {
      if (!a || !b) return;

      if (a.broken || b.broken) {
        return;
      }

      const dx = b.x - a.x;
      const dy = b.y - a.y;

      const distance =
        Math.sqrt(
          dx * dx + dy * dy
        );

      const minDistance =
        a.radius + b.radius;

      if (
        distance >= minDistance ||
        distance === 0
      ) {
        return;
      }

      /*
       * Bigger ball can destroy
       * smaller ball.
       */

      const bigger =
        a.radius > b.radius
          ? a
          : b;

      const smaller =
        a.radius > b.radius
          ? b
          : a;

      const sizeDifference =
        bigger.radius /
        smaller.radius;

      if (
        sizeDifference > 1.35
      ) {
        const smallerIndex =
          balls.indexOf(
            smaller
          );

        if (
          smallerIndex !== -1
        ) {
          const collisionSpeed =
            Math.abs(
              a.vx - b.vx
            ) +
            Math.abs(
              a.vy - b.vy
            );

          if (
            collisionSpeed > 1.2
          ) {
            breakBall(
              smallerIndex,
              smaller.points
            );

            return;
          }
        }
      }

      /*
       * Normal elastic-style bounce.
       */

      const nx =
        dx / distance;

      const ny =
        dy / distance;

      const overlap =
        minDistance - distance;

      a.x -=
        nx * overlap * 0.5;

      a.y -=
        ny * overlap * 0.5;

      b.x +=
        nx * overlap * 0.5;

      b.y +=
        ny * overlap * 0.5;

      const relativeVelocityX =
        a.vx - b.vx;

      const relativeVelocityY =
        a.vy - b.vy;

      const velocityAlongNormal =
        relativeVelocityX * nx +
        relativeVelocityY * ny;

      if (
        velocityAlongNormal > 0
      ) {
        return;
      }

      const impulse =
        velocityAlongNormal;

      a.vx -=
        impulse * nx;

      a.vy -=
        impulse * ny;

      b.vx +=
        impulse * nx;

      b.vy +=
        impulse * ny;
    };

    /*
     * ---------------------------------------------------------
     * UPDATE BALLS
     * ---------------------------------------------------------
     */

    const updateBalls = () => {
      /*
       * Movement + mouse interaction.
       */

      for (
        let i = balls.length - 1;
        i >= 0;
        i--
      ) {
        const ball = balls[i];

        if (!ball || ball.broken) {
          continue;
        }

        ball.x += ball.vx;
        ball.y += ball.vy;

        ball.rotation += 0.01;
        ball.pulse += 0.04;

        /*
         * Wall bounce - left.
         */

        if (
          ball.x -
            ball.radius <=
          0
        ) {
          ball.x =
            ball.radius;

          ball.vx =
            Math.abs(
              ball.vx
            );
        }

        /*
         * Wall bounce - right.
         */

        if (
          ball.x +
            ball.radius >=
          width
        ) {
          ball.x =
            width -
            ball.radius;

          ball.vx =
            -Math.abs(
              ball.vx
            );
        }

        /*
         * Wall bounce - top.
         */

        if (
          ball.y -
            ball.radius <=
          0
        ) {
          ball.y =
            ball.radius;

          ball.vy =
            Math.abs(
              ball.vy
            );
        }

        /*
         * Wall bounce - bottom.
         */

        if (
          ball.y +
            ball.radius >=
          height
        ) {
          ball.y =
            height -
            ball.radius;

          ball.vy =
            -Math.abs(
              ball.vy
            );
        }

        /*
         * Mouse hover break.
         */

        if (mouse.active) {
          const dx =
            ball.x - mouse.x;

          const dy =
            ball.y - mouse.y;

          const distance =
            Math.sqrt(
              dx * dx +
                dy * dy
            );

          if (
            distance <
            ball.radius +
              mouseBreakRadius
          ) {
            breakBall(
              i,
              ball.points
            );
          }
        }
      }

      /*
       * Ball-to-ball collisions.
       *
       * Because a ball can be removed during
       * collision handling, we always check
       * that both objects still exist.
       */

      for (
        let i = 0;
        i < balls.length;
        i++
      ) {
        const a = balls[i];

        if (!a || a.broken) {
          continue;
        }

        for (
          let j = i + 1;
          j < balls.length;
          j++
        ) {
          const b = balls[j];

          if (
            !b ||
            b.broken
          ) {
            continue;
          }

          handleBallCollision(
            a,
            b
          );

          /*
           * a may still exist,
           * but b could have been removed.
           */

          if (
            !balls.includes(a)
          ) {
            break;
          }
        }
      }
    };

    /*
     * ---------------------------------------------------------
     * UPDATE PARTICLES
     * ---------------------------------------------------------
     */

    const updateParticles = () => {
      for (
        let i =
          particles.length - 1;
        i >= 0;
        i--
      ) {
        const particle =
          particles[i];

        if (!particle) {
          particles.splice(i, 1);
          continue;
        }

        particle.x +=
          particle.vx;

        particle.y +=
          particle.vy;

        particle.vx *= 0.97;
        particle.vy *= 0.97;

        particle.vy += 0.025;

        particle.life -= 0.025;

        if (
          particle.life <= 0
        ) {
          particles.splice(
            i,
            1
          );
        }
      }

      /*
       * Final safety cap.
       */

      if (
        particles.length >
        MAX_PARTICLES
      ) {
        particles.splice(
          0,
          particles.length -
            MAX_PARTICLES
        );
      }
    };

    /*
     * ---------------------------------------------------------
     * DRAW BALL
     * ---------------------------------------------------------
     */

    const drawBall = (ball) => {
      const {
        x,
        y,
        radius,
        color,
        points,
      } = ball;

      /*
       * Shadow.
       */

      ctx.save();

      ctx.beginPath();

      ctx.ellipse(
        x + radius * 0.15,
        y + radius * 0.95,
        radius * 0.8,
        radius * 0.22,
        0,
        0,
        Math.PI * 2
      );

      ctx.fillStyle =
        "rgba(0,0,0,0.10)";

      ctx.filter =
        "blur(4px)";

      ctx.fill();

      ctx.restore();

      /*
       * 3D gradient.
       */

      ctx.save();

      const gradient =
        ctx.createRadialGradient(
          x - radius * 0.35,
          y - radius * 0.4,
          radius * 0.05,
          x,
          y,
          radius
        );

      gradient.addColorStop(
        0,
        "#ffffff"
      );

      gradient.addColorStop(
        0.12,
        color.light
      );

      gradient.addColorStop(
        0.45,
        color.main
      );

      gradient.addColorStop(
        0.82,
        color.dark
      );

      gradient.addColorStop(
        1,
        "#250000"
      );

      ctx.beginPath();

      ctx.arc(
        x,
        y,
        radius,
        0,
        Math.PI * 2
      );

      ctx.fillStyle =
        gradient;

      ctx.shadowColor =
        `${color.main}55`;

      ctx.shadowBlur = 14;

      ctx.fill();

      ctx.restore();

      /*
       * Inner reflection.
       */

      ctx.save();

      ctx.beginPath();

      ctx.arc(
        x - radius * 0.3,
        y - radius * 0.32,
        radius * 0.18,
        0,
        Math.PI * 2
      );

      const shine =
        ctx.createRadialGradient(
          x - radius * 0.3,
          y - radius * 0.32,
          0,
          x - radius * 0.3,
          y - radius * 0.32,
          radius * 0.18
        );

      shine.addColorStop(
        0,
        "rgba(255,255,255,0.95)"
      );

      shine.addColorStop(
        1,
        "rgba(255,255,255,0)"
      );

      ctx.fillStyle = shine;

      ctx.fill();

      ctx.restore();

      /*
       * Small highlight.
       */

      ctx.save();

      ctx.beginPath();

      ctx.arc(
        x + radius * 0.25,
        y + radius * 0.2,
        radius * 0.08,
        0,
        Math.PI * 2
      );

      ctx.fillStyle =
        "rgba(255,255,255,0.35)";

      ctx.fill();

      ctx.restore();

      /*
       * -------------------------------------------------------
       * POINTS
       *
       * IMPORTANT:
       * Points are drawn AFTER the ball gradient,
       * so they are visible.
       * -------------------------------------------------------
       */

      ctx.save();

      ctx.font = `800 ${Math.max(
        10,
        radius * 0.55
      )}px Inter, Arial, sans-serif`;

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillStyle = "#ffffff";

      ctx.shadowColor =
        "rgba(0,0,0,0.45)";

      ctx.shadowBlur = 4;

      ctx.fillText(
        `+${points}`,
        x,
        y
      );

      ctx.restore();
    };

    /*
     * ---------------------------------------------------------
     * DRAW PARTICLES
     * ---------------------------------------------------------
     */

    const drawParticles = () => {
      for (const particle of particles) {
        ctx.save();

        ctx.globalAlpha =
          Math.max(
            0,
            particle.life
          );

        ctx.beginPath();

        ctx.arc(
          particle.x,
          particle.y,
          particle.size *
            particle.life,
          0,
          Math.PI * 2
        );

        ctx.fillStyle =
          particle.color;

        ctx.shadowColor =
          particle.color;

        ctx.shadowBlur = 8;

        ctx.fill();

        ctx.restore();
      }
    };

    /*
     * ---------------------------------------------------------
     * DRAW MOUSE TARGET
     * ---------------------------------------------------------
     */

    const drawMouseTarget = () => {
      if (!mouse.active) {
        return;
      }

      ctx.save();

      /*
       * Outer circle.
       */

      ctx.beginPath();

      ctx.arc(
        mouse.x,
        mouse.y,
        clickBreakRadius,
        0,
        Math.PI * 2
      );

      ctx.strokeStyle =
        "rgba(215,25,32,0.08)";

      ctx.lineWidth = 1;

      ctx.stroke();

      /*
       * Inner circle.
       */

      ctx.beginPath();

      ctx.arc(
        mouse.x,
        mouse.y,
        mouseBreakRadius,
        0,
        Math.PI * 2
      );

      ctx.strokeStyle =
        "rgba(215,25,32,0.25)";

      ctx.lineWidth = 1.5;

      ctx.stroke();

      /*
       * Cursor center.
       */

      ctx.beginPath();

      ctx.arc(
        mouse.x,
        mouse.y,
        3,
        0,
        Math.PI * 2
      );

      ctx.fillStyle =
        "#d71920";

      ctx.fill();

      ctx.restore();
    };

    /*
     * ---------------------------------------------------------
     * LEVEL DISPLAY
     * ---------------------------------------------------------
     *
     * 0 - 999     = Level 1
     * 1000 -1999  = Level 2
     * 2000 -2999  = Level 3
     * etc.
     */

    const drawLevel = () => {
      ctx.save();

      const level =
        Math.floor(
          scoreRef.current / 1000
        ) + 1;

      const x = 24;
      const y = 30;

      /*
       * Background.
       */

      ctx.beginPath();

      ctx.roundRect(
        x,
        y - 20,
        125,
        38,
        19
      );

      ctx.fillStyle =
        "rgba(255,255,255,0.94)";

      ctx.shadowColor =
        "rgba(0,0,0,0.12)";

      ctx.shadowBlur = 10;

      ctx.fill();

      /*
       * Border.
       */

      ctx.shadowBlur = 0;

      ctx.strokeStyle =
        "rgba(215,25,32,0.20)";

      ctx.lineWidth = 1;

      ctx.stroke();

      /*
       * Text.
       */

      ctx.font =
        "800 14px Inter, Arial, sans-serif";

      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      ctx.fillStyle =
        "#d71920";

      ctx.fillText(
        `🏆 LEVEL ${level}`,
        x + 15,
        y
      );

      ctx.restore();
    };

    /*
     * ---------------------------------------------------------
     * SCORE DISPLAY
     * ---------------------------------------------------------
     */

    const drawScore = () => {
      ctx.save();

      ctx.font =
        "700 13px Inter, Arial, sans-serif";

      ctx.textAlign = "right";
      ctx.textBaseline = "middle";

      ctx.fillStyle =
        "rgba(30,30,30,0.50)";

      ctx.fillText(
        `BREAK: ${scoreRef.current.toLocaleString()}`,
        width - 24,
        30
      );

      ctx.restore();
    };

    /*
     * ---------------------------------------------------------
     * BACKGROUND
     * ---------------------------------------------------------
     */

    const drawBackground = () => {
      ctx.save();

      ctx.fillStyle =
        "rgba(215,25,32,0.035)";

      for (
        let x = 20;
        x < width;
        x += 45
      ) {
        for (
          let y = 20;
          y < height;
          y += 45
        ) {
          ctx.beginPath();

          ctx.arc(
            x,
            y,
            1,
            0,
            Math.PI * 2
          );

          ctx.fill();
        }
      }

      ctx.restore();
    };

    /*
     * ---------------------------------------------------------
     * MAIN DRAW
     * ---------------------------------------------------------
     */

    const draw = () => {
      ctx.clearRect(
        0,
        0,
        width,
        height
      );

      drawBackground();

      /*
       * Balls.
       */

      for (const ball of balls) {
        if (!ball.broken) {
          drawBall(ball);
        }
      }

      /*
       * Particles.
       */

      drawParticles();

      /*
       * Mouse target.
       */

      drawMouseTarget();

      /*
       * UI.
       */

      drawLevel();
      drawScore();
    };

    /*
     * ---------------------------------------------------------
     * ANIMATION LOOP
     * ---------------------------------------------------------
     */

    const animate = () => {
      updateBalls();

      updateParticles();

      draw();

      animationFrame =
        requestAnimationFrame(
          animate
        );
    };

    /*
     * ---------------------------------------------------------
     * START
     * ---------------------------------------------------------
     */
nextMilestoneRef.current = 1000;

resize();

if (gameStarted) {
  initializeBalls();

  canvas.addEventListener(
    "mousemove",
    handleMouseMove
  );

  canvas.addEventListener(
    "mouseleave",
    handleMouseLeave
  );

  canvas.addEventListener(
    "click",
    handleClick
  );

  window.addEventListener(
    "resize",
    resize
  );

  animate();
}







    /*
     * ---------------------------------------------------------
     * CLEANUP
     * ---------------------------------------------------------
     */

    return () => {
      /*
       * Stop animation.
       */

      if (
        animationFrame !== null
      ) {
        cancelAnimationFrame(
          animationFrame
        );
      }

      /*
       * Clear ALL pending timers.
       */

      timeoutRefs.current.forEach(
        (timeoutId) => {
          clearTimeout(timeoutId);
        }
      );

      timeoutRefs.current.clear();

      /*
       * Remove events.
       */

      canvas.removeEventListener(
        "mousemove",
        handleMouseMove
      );

      canvas.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );

      canvas.removeEventListener(
        "click",
        handleClick
      );

      window.removeEventListener(
        "resize",
        resize
      );

      /*
       * Release arrays.
       */

      balls.length = 0;
      particles.length = 0;
    };
  }, [
    gameStarted,
    ballCount,
    minRadius,
    maxRadius,
    speed,
    mouseBreakRadius,
    clickBreakRadius,
    disabledOnMobile,
  ]);

  /*
   * ---------------------------------------------------------
   * UI
   * ---------------------------------------------------------
   */

  return (
  <Box
    sx={{
      position: "absolute",
      inset: 0,

      width: "100%",
      height: "100%",

      zIndex,

      overflow: "hidden",

      pointerEvents: "auto",

      "& canvas": {
        display: "block",
        width: "100%",
        height: "100%",
        cursor: gameStarted
          ? "crosshair"
          : "default",
        touchAction: "none",
      },

      "@media (max-width:700px)": {
        display: disabledOnMobile
          ? "none"
          : "block",
      },

      /* GAME CONTROL - TOP RIGHT CORNER */
      "& .start-game-overlay": {
  position: "absolute",

  top:120,
  right: 20,

  display: gameStarted
    ? "none"
    : "block",

  zIndex: 200,

  pointerEvents: "none",
},

"& .start-game-button": {
  pointerEvents: "auto",

  borderRadius: "30px",

  px: 3,

  py: 1.1,

  fontSize: "14px",

  fontWeight: 800,

  textTransform: "none",

  background:
    "linear-gradient(135deg,#d71920,#8e0b10)",

  boxShadow:
    "0 8px 25px rgba(215,25,32,0.30)",

  "&:hover": {
    background:
      "linear-gradient(135deg,#ef2930,#a40d14)",

    transform:
      "translateY(-2px)",

    boxShadow:
      "0 12px 30px rgba(215,25,32,0.35)",
  },

  transition:
    "all 0.2s ease",
},


      /*
       * STOP GAME CONTROL
       * Appears in the same top-right corner as Start Game.
       */
      "& .stop-game-overlay": {
        position: "absolute",
        top: 120,
        right: 20,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        zIndex: 250,
        pointerEvents: "none",
      },

      "& .stop-game-button": {
        pointerEvents: "auto",

        minWidth: "150px",
        height: "44px",
        px: 2.5,

        borderRadius: "999px",

        fontSize: "14px",
        fontWeight: 800,
        letterSpacing: "0.2px",
        textTransform: "none",

        color: "#fff",

        background:
          "linear-gradient(135deg,#252525,#050505)",

        border:
          "1px solid rgba(255,255,255,0.30)",

        boxShadow:
          "0 10px 28px rgba(0,0,0,0.30)",

        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",

        "&:hover": {
          background:
            "linear-gradient(135deg,#444,#111)",

          transform:
            "translateY(-2px) scale(1.02)",

          boxShadow:
            "0 14px 34px rgba(0,0,0,0.38)",
        },

        "&:active": {
          transform: "translateY(0) scale(0.98)",
        },

        transition: "all 0.2s ease",
      },

      /*
       * CHEER MESSAGE
       */
      "& .cheer-message": {
        position: "absolute",

        top: "2%",
        left: "50%",

        transform:
          "translate(-50%, -50%) scale(0.5)",

        opacity: 0,

        pointerEvents: "none",

        padding: "20px 32px",

        borderRadius: "22px",

        background:
          "rgba(255,255,255,0.94)",

        border:
          "1px solid rgba(215,25,32,0.18)",

        boxShadow:
          "0 20px 60px rgba(0,0,0,0.16)",

        backdropFilter:
          "blur(14px)",

        WebkitBackdropFilter:
          "blur(14px)",

        textAlign: "center",

        whiteSpace: "nowrap",

        zIndex: 100,

        minWidth: {
          xs: 230,
          sm: 280,
        },
      },

      "& .cheer-icon": {
        fontSize: "42px",
        lineHeight: 1,
        marginBottom: "6px",
      },

      "& .cheer-title": {
        fontSize: {
          xs: "21px",
          sm: "25px",
        },

        fontWeight: 800,

        background:
          "linear-gradient(90deg,#d71920,#7b1fa2,#1565c0)",

        WebkitBackgroundClip: "text",

        WebkitTextFillColor:
          "transparent",
      },

      "& .cheer-subtitle": {
        marginTop: "5px",

        fontSize: "13px",

        color: "#666",

        fontWeight: 600,
      },

      "@keyframes cheerPop": {
        "0%": {
          opacity: 0,

          transform:
            "translate(-50%, -50%) scale(0.5)",
        },

        "12%": {
          opacity: 1,

          transform:
            "translate(-50%, -50%) scale(1.12)",
        },

        "22%": {
          transform:
            "translate(-50%, -50%) scale(0.98)",
        },

        "30%": {
          transform:
            "translate(-50%, -50%) scale(1)",
        },

        "75%": {
          opacity: 1,

          transform:
            "translate(-50%, -50%) scale(1)",
        },

        "100%": {
          opacity: 0,

          transform:
            "translate(-50%, -50%) scale(0.9)",
        },
      },
    }}
  >
    <canvas ref={canvasRef} />

    {/* START GAME */}
   {!gameStarted && (
  <Box className="start-game-overlay">
    <Button
      className="start-game-button"
      variant="contained"
      onClick={() => setGameStarted(true)}
    >
      🚀 Start Game to Time Pass
    </Button> 
  </Box>
)}

{gameStarted && (
  <Box className="stop-game-overlay">
    <Button
      className="stop-game-button"
      variant="contained"
      onClick={stopGame}
    >
      ⏹ Stop Game
    </Button>
  </Box>
)}


    {/* Celebration */}
    <Box
      ref={cheerRef}
      className="cheer-message"
    >
      <Box className="cheer-icon">
        🎉
      </Box>

      <Box className="cheer-title">
        AMAZING!
      </Box>

      <Box className="cheer-subtitle">
        You reached a new milestone!
      </Box>
    </Box>
  </Box>
);
};

export default BouncingBallGame;